/*
 * Where Research lives.
 *
 *   /research                       the library's landing (coverage, latest updates)
 *   /research?q=<query>             All Sources for a query
 *   /research?source=<id>&q=<query> one source, with its filters
 *     (&circuit= for Case Law; &origin=, &year=, &form= for Unpublished)
 *   ...&doc=<id>                    a document open in the reader
 *
 * No React or IO here so the rules stay unit-testable.
 */
import type { ResearchSourceId } from "./types";
import { RESEARCH_SOURCE_IDS } from "./sources";

export const RESEARCH_PATH = "/research";

export type ResearchFilters = {
  circuit?: string;
  origin?: string;
  year?: string;
  form?: string;
};

export type ResearchView = {
  query: string;
  /** `null` is All Sources. */
  source: ResearchSourceId | null;
  filters: ResearchFilters;
  docId: string | null;
};

const FILTER_KEYS = ["circuit", "origin", "year", "form"] as const;

const isSource = (value: string | null): value is ResearchSourceId =>
  value !== null && (RESEARCH_SOURCE_IDS as readonly string[]).includes(value);

export const parseResearchView = (search: string): ResearchView => {
  const params = new URLSearchParams(search);
  const source = params.get("source");
  const filters: ResearchFilters = {};
  for (const key of FILTER_KEYS) {
    const value = params.get(key)?.trim();
    if (value) filters[key] = value;
  }
  return {
    query: params.get("q")?.trim() ?? "",
    source: isSource(source) ? source : null,
    filters,
    docId: params.get("doc")?.trim() || null
  };
};

export const researchPath = (view: Partial<ResearchView> = {}): string => {
  const params = new URLSearchParams();
  if (view.source) params.set("source", view.source);
  if (view.query?.trim()) params.set("q", view.query.trim());
  for (const key of FILTER_KEYS) {
    const value = view.filters?.[key]?.trim();
    if (value) params.set(key, value);
  }
  if (view.docId) params.set("doc", view.docId);
  const query = params.toString();
  return query ? `${RESEARCH_PATH}?${query}` : RESEARCH_PATH;
};
